import { EquipmentTypeBadge } from '@/components/badges/EquipmentTypeBadge';
import { type DataTableColumn, DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { allEquipmentTypesState, equipmentTypeFamiliesState } from '@/state';
import { useAsyncAtomValue, useLoadableAtomValue } from '@/state/loadableHooks';
import type { EquipmentTypeFamily } from '@rock/types';
import { type FC, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FamilyDialog } from './FamilyDialog';

export const FAMILIES_PATH = '/equipment/types/families';

/** One family's page. */
export function familyPath(familyId: string): string {
    return `${FAMILIES_PATH}/${encodeURIComponent(familyId)}`;
}

/**
 * Type families: catalog types one product may be filed under, which both matchers read as one tier.
 * A row's id in the URL opens it for editing; `new` opens a blank one.
 */
export const FamiliesTab: FC = () => {
    const [families, loadable] = useLoadableAtomValue(equipmentTypeFamiliesState, []);
    const types = useAsyncAtomValue(allEquipmentTypesState, []);
    const typeTitles = useMemo(() => new Map(types.map((type) => [type.id, type.title])), [types]);
    const { familyId } = useParams<{ familyId: string }>();
    const navigate = useNavigate();
    const selected = familyId && familyId !== 'new' ? families.find((family) => family.id === familyId) : undefined;
    const columns = useMemo(() => familyColumns(typeTitles), [typeTitles]);

    return (
        <>
            <div className="flex items-center justify-between gap-2">
                <p className="text-sm text-muted-foreground">
                    A unit of one type may join a catalog model of another type in its family, flagged for review; a
                    recall listing one type reaches units of the others by asking the owner the type.
                </p>
                <Button type="button" onClick={() => navigate(`${FAMILIES_PATH}/new`)}>
                    Add family
                </Button>
            </div>
            {loadable.state !== 'hasData' ? (
                <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : families.length === 0 ? (
                <div className="rounded border border-dashed p-8 text-center text-sm text-muted-foreground">
                    No families yet. Add one when two catalog types turn out to be filings of one product.
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    rows={families}
                    rowKey={(family) => family.id}
                    rowHref={(family) => familyPath(family.id)}
                />
            )}
            {(familyId === 'new' || selected) && (
                <FamilyDialog
                    open
                    onOpenChange={(open) => !open && navigate(FAMILIES_PATH)}
                    family={selected}
                    families={families}
                />
            )}
        </>
    );
};

function familyColumns(typeTitles: Map<string, string>): DataTableColumn<EquipmentTypeFamily>[] {
    return [
        { key: 'id', header: 'ID', className: 'min-w-[140px] font-mono', cell: (family) => family.id },
        { key: 'name', header: 'Name', className: 'min-w-[160px] font-medium', cell: (family) => family.name },
        {
            key: 'types',
            header: 'Types',
            className: 'min-w-[320px]',
            cell: (family) => (
                <span className="flex flex-wrap gap-1">
                    {family.typeIds.map((typeId) => (
                        <EquipmentTypeBadge key={typeId} typeId={typeId} title={typeTitles.get(typeId)} />
                    ))}
                </span>
            ),
        },
        {
            key: 'updatedAt',
            header: 'Last updated',
            className: 'min-w-[180px]',
            cell: (family) => new Date(family.updatedAt).toLocaleString(),
        },
    ];
}
